import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import {
  Icon,
  LoadingState,
  Select,
  StatePanel,
  V2Alert,
  V2Badge,
  V2Button,
  V2DataGrid,
  V2EmptyState,
  V2FormActions,
  V2FormField,
  V2FormGrid,
  V2Modal,
  V2Tabs,
  type V2BadgeTone,
  type V2GridColumn,
  type V2GridTone,
} from '@sakhtar/design-system/react';
import { apiPost, ApiError } from '../../lib/apiClient.js';
import { tenantBasePath } from '../../lib/tenant.js';
import type {
  CallDetailResponse,
  CallSentimentStage,
  CommunicationDashboardItem,
  CommunicationDashboardResponse,
  CustomerCommunicationEvent,
} from '../../lib/types/customers.js';
import { useApiGet } from '../../lib/useApiGet.js';
import { HubKpi, HubKpiGrid } from '../../components/hub/HubKpi.js';
import { CreditTopupDialog } from './CreditTopupDialog.js';
import { CustomerCreateDialog } from './CustomerCreateDialog.js';
import { LeadFormPanel } from './leads/LeadFormPanel.js';
import { callContactOf } from './callContact.js';
import { PartyLink } from './PartyLink.js';
import { AnalyzingPanel, YarFace } from './SalesYar.js';
import './callBasePage.css';

const PAGE_SIZE = 50;

const EVENT_LABELS: Record<CustomerCommunicationEvent, string> = {
  queued: 'در صف',
  provider_accepted: 'پذیرفته‌شده',
  delivered: 'تحویل‌شده',
  read: 'خوانده‌شده',
  failed: 'ناموفق',
  cancelled: 'لغوشده',
  received: 'دریافت‌شده',
  started: 'شروع‌شده',
  answered: 'پاسخ‌داده‌شده',
  completed: 'تکمیل‌شده',
  missed: 'بی‌پاسخ',
  analysis_completed: 'تحلیل‌شده',
};

const OUTCOME_LABELS: Record<string, string> = {
  resolved: 'حل‌شده و پایان‌یافته',
  follow_up_needed: 'نیازمند پیگیری',
  transferred: 'انتقال‌یافته به بخش دیگر',
  unresolved: 'حل‌نشده',
  deal_progressed: 'پیشرفت در معامله',
  lost: 'فرصت از دست رفته',
  information_provided: 'ارائه اطلاعات',
};

const SENTIMENT_LABELS: Record<string, string> = {
  positive: 'مثبت و راضی',
  neutral: 'خنثی و رسمی',
  negative: 'ناراضی و معترض',
  mixed: 'متغیر',
};

const RISK_LABELS: Record<string, string> = {
  low: 'ریسک کم',
  medium: 'ریسک متوسط',
  high: 'ریسک بالا',
  critical: 'ریسک بحرانی',
};

const RECORDING_STATUS_LABELS: Record<string, string> = {
  available: 'موجود',
  processing: 'در حال پردازش',
  pending: 'در انتظار',
  not_available: 'بدون ضبط',
  failed: 'خطا در دریافت',
  purged: 'حذف‌شده بر اساس Retention',
};

const TRANSCRIPT_STATUS_LABELS: Record<string, string> = {
  completed: 'متن موجود',
  processing: 'در حال تبدیل',
  queued: 'در صف تبدیل',
  not_available: 'بدون متن',
  failed: 'خطا در تبدیل',
};

const ANALYSIS_STATUS_LABELS: Record<string, string> = {
  completed: 'تحلیل‌شده',
  processing: 'در حال تحلیل',
  queued: 'در صف تحلیل',
  waiting_for_transcript: 'در انتظار متن',
  waiting_for_recording: 'در انتظار صوت',
  failed: 'تحلیل ناموفق',
  needs_review: 'نیازمند بازبینی',
  pending_confirmation: 'منتظر تأیید مصرف توکن',
  insufficient_balance: 'کسری اعتبار کیف پول',
  not_available: 'در انتظار تحلیل',
};

function faNumber(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '۰';
  return value.toLocaleString('fa-IR');
}

function formatDuration(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined || Number.isNaN(seconds)) return '—';
  const rounded = Math.max(0, Math.round(seconds));
  const minutes = Math.floor(rounded / 60);
  const rest = rounded % 60;
  return `${faNumber(minutes)}:${rest.toLocaleString('fa-IR', {
    minimumIntegerDigits: 2,
    useGrouping: false,
  })}`;
}

function formatDate(value: string | null): string {
  if (!value) return '—';
  return new Intl.DateTimeFormat('fa-IR-u-ca-persian', {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(value));
}

function eventTone(event: CustomerCommunicationEvent): V2GridTone {
  if (event === 'failed' || event === 'missed') return 'danger';
  if (event === 'cancelled') return 'warn';
  if (event === 'completed' || event === 'analysis_completed') return 'success';
  if (event === 'answered' || event === 'started' || event === 'received') return 'info';
  return 'neutral';
}

function sentimentTone(sentiment: string | null): V2BadgeTone {
  if (sentiment === 'positive') return 'success';
  if (sentiment === 'negative') return 'danger';
  if (sentiment === 'mixed') return 'warn';
  return 'neutral';
}

function riskTone(risk: string | null): V2BadgeTone {
  if (risk === 'critical' || risk === 'high') return 'danger';
  if (risk === 'medium') return 'warn';
  return 'success';
}

function recordingTone(status: string): V2BadgeTone {
  if (status === 'available') return 'success';
  if (status === 'processing' || status === 'pending') return 'info';
  if (status === 'failed') return 'danger';
  return 'neutral';
}

function analysisTone(status: string): V2BadgeTone {
  if (status === 'completed') return 'success';
  if (status === 'failed' || status === 'insufficient_balance') return 'danger';
  if (
    status === 'needs_review' ||
    status === 'waiting_for_transcript' ||
    status === 'waiting_for_recording' ||
    status === 'pending_confirmation'
  ) {
    return 'warn';
  }
  if (status === 'queued' || status === 'processing') return 'info';
  return 'neutral';
}

function AudioPlayer({
  streamUrl,
  downloadUrl,
}: {
  readonly streamUrl: string;
  readonly downloadUrl: string;
}) {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);

  function togglePlay() {
    if (!audioRef.current) return;
    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play().catch(() => {});
    }
  }

  return (
    <div className="call-player" aria-label="پخش‌کننده مکالمه">
      <audio
        ref={audioRef}
        src={streamUrl}
        preload="metadata"
        onPlay={() => setIsPlaying(true)}
        onPause={() => setIsPlaying(false)}
        onTimeUpdate={() => {
          if (audioRef.current) setCurrentTime(audioRef.current.currentTime);
        }}
        onLoadedMetadata={() => {
          if (audioRef.current) setDuration(audioRef.current.duration);
        }}
      />
      <div className="call-player__controls">
        <button
          type="button"
          className="call-player__play-btn sk-btn sk-btn--primary sk-btn--sm"
          onClick={togglePlay}
          aria-label={isPlaying ? 'توقف پخش' : 'شروع پخش'}
        >
          {isPlaying ? '⏸ توقف' : '▶ پخش مکالمه'}
        </button>
        <div className="call-player__timeline">
          <input
            type="range"
            min={0}
            max={duration || 100}
            value={currentTime}
            onChange={(e) => {
              const val = Number(e.target.value);
              setCurrentTime(val);
              if (audioRef.current) audioRef.current.currentTime = val;
            }}
            className="call-player__slider"
            aria-label="موقعیت زمانی فایل مکالمه"
          />
          <div className="call-player__times">
            <span>{formatDuration(currentTime)}</span>
            <span>{formatDuration(duration)}</span>
          </div>
        </div>
        <a
          href={downloadUrl}
          download
          className="sk-btn sk-btn--secondary sk-btn--sm call-player__download"
          title="دانلود فایل صوتی مکالمه"
        >
          ⬇ دانلود فایل
        </a>
      </div>
    </div>
  );
}

function TranscriptView({
  rawText,
  segments,
}: {
  readonly rawText: string | null;
  readonly segments: CallDetailResponse['transcript']['segments'];
}) {
  const [search, setSearch] = useState('');

  const filteredSegments = (segments ?? []).filter((s) =>
    !search.trim() ? true : s.text.toLowerCase().includes(search.toLowerCase()),
  );

  return (
    <div className="call-transcript">
      <div className="call-transcript__search">
        <input
          type="search"
          placeholder="جست‌وجو در متن مکالمه…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="sk-input sk-input--sm"
        />
      </div>
      {(segments?.length ?? 0) > 0 ? (
        <div className="call-transcript__list">
          {filteredSegments.map((s, idx) => (
            <div
              key={idx}
              className={`call-transcript__bubble call-transcript__bubble--${s.speaker}`}
            >
              <div className="call-transcript__meta">
                <span className="call-transcript__speaker">
                  {s.speaker === 'agent'
                    ? 'کارشناس'
                    : s.speaker === 'customer'
                      ? 'مشتری'
                      : 'گوینده'}
                </span>
                <span className="call-transcript__time">{formatDuration(s.startTime)}</span>
              </div>
              <p className="call-transcript__text">{s.text}</p>
            </div>
          ))}
          {filteredSegments.length === 0 ? (
            <p className="call-transcript__empty">عبارتی مطابق جست‌وجو پیدا نشد.</p>
          ) : null}
        </div>
      ) : (
        <div className="call-transcript__raw">
          <p>{rawText}</p>
        </div>
      )}
    </div>
  );
}

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const res = reader.result;
      if (typeof res === 'string') {
        const commaIdx = res.indexOf(',');
        resolve(commaIdx >= 0 ? res.slice(commaIdx + 1) : res);
      } else {
        reject(new Error('قالب فایل خوانده‌شده نامعتبر است.'));
      }
    };
    reader.onerror = () => reject(new Error('خطا در خواندن فایل صوتی'));
    reader.readAsDataURL(file);
  });
}

export function CallBasePage() {
  const { slug } = useParams<{ slug: string }>();
  const base = slug ? tenantBasePath(slug) : '';
  const [params, setParams] = useSearchParams();

  const search = params.get('q') ?? '';
  const direction = params.get('direction') ?? '';
  const status = params.get('status') ?? '';
  const answeredOrMissed = params.get('answeredOrMissed') ?? '';
  const hasRecording = params.get('hasRecording') ?? '';
  const analysis = params.get('analysis') ?? '';
  const outcome = params.get('outcome') ?? '';
  const riskLevel = params.get('riskLevel') ?? '';
  // Set by a salesperson's profile: show only the calls that person handled.
  const operator = params.get('operator') ?? '';
  const offset = Math.max(Number(params.get('offset') ?? 0) || 0, 0);
  const selectedRef = params.get('call') ?? '';

  const [detailTab, setDetailTab] = useState<string>('info');
  const [actionLoading, setActionLoading] = useState(false);
  const [topupOpen, setTopupOpen] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  // The four tabs overflow a phone-width sheet: keep the active one in view (opening from the
  // «تحلیل فروش‌یار» row button lands on the last tab).
  useEffect(() => {
    if (!selectedRef) return;
    const frame = requestAnimationFrame(() => {
      document
        .querySelector('#call-detail-panel [aria-selected="true"]')
        ?.scrollIntoView({ inline: 'nearest', block: 'nearest' });
    });
    return () => cancelAnimationFrame(frame);
  }, [selectedRef, detailTab]);

  // «+» on an unknown caller: choose customer or lead, create it with the existing forms,
  // then link this call to what was created.
  const [identify, setIdentify] = useState<{
    readonly row: CommunicationDashboardItem;
    readonly step: 'choose' | 'customer' | 'lead';
  } | null>(null);
  const [identifyNotice, setIdentifyNotice] = useState<{
    readonly tone: 'success' | 'danger';
    readonly text: string;
  } | null>(null);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [uploadFiles, setUploadFiles] = useState<File[]>([]);
  const [uploadDirection, setUploadDirection] = useState<'inbound' | 'outbound'>('inbound');
  const [uploadExternalNumber, setUploadExternalNumber] = useState('');
  const [uploadExtension, setUploadExtension] = useState('101');
  const [uploadLoading, setUploadLoading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [batchResults, setBatchResults] = useState<{
    readonly total: number;
    readonly successful: number;
    readonly failed: number;
    readonly results?: readonly {
      readonly index: number;
      readonly success: boolean;
      readonly error?: string;
    }[];
  } | null>(null);

  const request = new URLSearchParams({
    channel: 'voice',
    offset: String(offset),
    limit: String(PAGE_SIZE),
  });
  if (search.trim()) request.set('search', search.trim());
  if (direction) request.set('direction', direction);
  if (status) request.set('status', status);
  if (answeredOrMissed) request.set('answeredOrMissed', answeredOrMissed);
  if (hasRecording) request.set('hasRecording', hasRecording);
  if (analysis) request.set('analysis', analysis);
  if (outcome) request.set('outcome', outcome);
  if (riskLevel) request.set('riskLevel', riskLevel);
  if (operator) request.set('operatorId', operator);

  const dashboard = useApiGet<CommunicationDashboardResponse>(
    slug ? `${base}/api/v1/crm/communications/dashboard?${request.toString()}` : null,
    [
      slug,
      search,
      direction,
      status,
      answeredOrMissed,
      hasRecording,
      analysis,
      outcome,
      riskLevel,
      operator,
      offset,
    ],
  );

  const callDetail = useApiGet<CallDetailResponse>(
    slug && selectedRef
      ? `${base}/api/v1/crm/communications/${encodeURIComponent(selectedRef)}`
      : null,
    [slug, selectedRef],
  );

  if (!slug) return null;

  function patchParams(patch: Record<string, string | null>) {
    const next = new URLSearchParams(params);
    for (const [key, value] of Object.entries(patch)) {
      if (!value) next.delete(key);
      else next.set(key, value);
    }
    setParams(next, { replace: true });
  }

  async function handleTriggerAnalysis(isRetry = false, confirmed = false) {
    if (!selectedRef) return;
    setActionLoading(true);
    setActionError(null);
    try {
      const path = `${base}/api/v1/crm/communications/${encodeURIComponent(selectedRef)}/analysis/${
        isRetry ? 'retry' : 'execute'
      }`;
      await apiPost(
        path,
        { confirmed },
        {
          headers: {
            'idempotency-key': `req-ai-${selectedRef}-${Date.now()}`,
          },
        },
      );
      callDetail.reload();
      dashboard.reload();
    } catch (err: unknown) {
      let message = 'خطا در اجرای تحلیل هوشمند';
      if (err instanceof ApiError) {
        const detailMsg = err.envelope?.error?.details?.[0]?.message;
        message = detailMsg || err.envelope?.error?.message || err.message;
      } else if (err instanceof Error) {
        message = err.message;
      }
      setActionError(message);
    } finally {
      setActionLoading(false);
    }
  }

  async function handleUploadSubmit(e: FormEvent) {
    e.preventDefault();
    if (uploadFiles.length === 0) {
      setUploadError('لطفاً حداقل یک فایل صوتی انتخاب کنید.');
      return;
    }
    if (!uploadExternalNumber.trim()) {
      setUploadError('شماره تلفن طرف مقابل الزامی است.');
      return;
    }
    setUploadLoading(true);
    setUploadError(null);
    setBatchResults(null);
    try {
      if (uploadFiles.length === 1) {
        const file = uploadFiles[0]!;
        const contentBase64 = await fileToBase64(file);
        const res = await apiPost<CallDetailResponse>(`${base}/api/v1/crm/communications/upload`, {
          fileName: file.name,
          contentBase64,
          mimeType: file.type || 'audio/mpeg',
          direction: uploadDirection,
          externalNumber: uploadExternalNumber.trim(),
          internalExtension: uploadExtension.trim() || undefined,
        });
        setUploadModalOpen(false);
        setUploadFiles([]);
        setUploadExternalNumber('');
        dashboard.reload();
        if (res?.ref) {
          patchParams({ call: res.ref });
        }
      } else {
        const items = await Promise.all(
          uploadFiles.map(async (file) => {
            const contentBase64 = await fileToBase64(file);
            return {
              fileName: file.name,
              contentBase64,
              mimeType: file.type || 'audio/mpeg',
              direction: uploadDirection,
              externalNumber: uploadExternalNumber.trim(),
              internalExtension: uploadExtension.trim() || undefined,
            };
          }),
        );
        const res = await apiPost<{
          readonly total: number;
          readonly successful: number;
          readonly failed: number;
          readonly results?: readonly {
            readonly index: number;
            readonly success: boolean;
            readonly error?: string;
          }[];
        }>(`${base}/api/v1/crm/communications/batch-upload`, { items });
        setBatchResults(res);
        dashboard.reload();
        if (res.failed === 0) {
          setUploadModalOpen(false);
          setUploadFiles([]);
          setUploadExternalNumber('');
        }
      }
    } catch (err: unknown) {
      let message = 'خطا در بارگذاری فایل مکالمه';
      if (err instanceof ApiError) {
        const detailMsg = err.envelope?.error?.details?.[0]?.message;
        message = detailMsg || err.envelope?.error?.message || err.message;
      } else if (err instanceof Error) {
        message = err.message;
      }
      setUploadError(message);
    } finally {
      setUploadLoading(false);
    }
  }

  async function handleReviewAnalysis() {
    if (!selectedRef) return;
    setActionLoading(true);
    setActionError(null);
    try {
      const path = `${base}/api/v1/crm/communications/${encodeURIComponent(selectedRef)}/analysis/review`;
      await apiPost(path, { reason: 'بررسی مجدد توسط مدیر فروش' });
      callDetail.reload();
      dashboard.reload();
    } catch (err: unknown) {
      let message = 'خطا در ثبت وضعیت بررسی';
      if (err instanceof ApiError) {
        const detailMsg = err.envelope?.error?.details?.[0]?.message;
        message = detailMsg || err.envelope?.error?.message || err.message;
      } else if (err instanceof Error) {
        message = err.message;
      }
      setActionError(message);
    } finally {
      setActionLoading(false);
    }
  }

  if (dashboard.status === 'loading') return <LoadingState />;
  if (dashboard.status === 'error') {
    return (
      <StatePanel
        variant={dashboard.error.status === 403 ? 'permission_denied' : 'network_error'}
        title={
          dashboard.error.status === 403
            ? 'برای مشاهده پایگاه تماس دسترسی ندارید'
            : 'ارتباط با سامانه برقرار نشد'
        }
        description={
          dashboard.error.status === 403
            ? 'برای بررسی این صفحه به دسترسی crm.communication.read و دسترسی مشتریان نیاز است.'
            : 'امکان دریافت فهرست تماس‌های سازمان وجود ندارد. دوباره تلاش کنید.'
        }
      />
    );
  }

  const summary = dashboard.data.summary;
  const selected = callDetail.status === 'ready' ? callDetail.data : null;
  const openDetail = (ref: string, tab: 'info' | 'analysis') => {
    setDetailTab(tab);
    setActionError(null);
    patchParams({ call: ref });
  };
  const closeDetail = () => patchParams({ call: null });

  /** Links the call to the customer/lead that was just created for its number. */
  async function linkIdentified(kind: 'customer' | 'lead', targetRef: string | undefined) {
    const target = identify?.row;
    setIdentify(null);
    if (!target) return;
    if (!targetRef) {
      setIdentifyNotice({
        tone: 'danger',
        text: 'مخاطب ساخته شد اما شناسه آن دریافت نشد؛ اتصال به تماس را از جزئیات تماس انجام دهید.',
      });
      dashboard.reload();
      return;
    }
    try {
      await apiPost(
        `${base}/api/v1/crm/communications/${encodeURIComponent(target.ref)}/relink`,
        { targetKind: kind, targetRef },
        { headers: { 'idempotency-key': `relink-${target.ref}-${targetRef}` } },
      );
      setIdentifyNotice({
        tone: 'success',
        text:
          kind === 'customer'
            ? 'مشتری جدید ساخته و به این تماس متصل شد.'
            : 'سرنخ جدید ساخته و به این تماس متصل شد.',
      });
    } catch (err: unknown) {
      setIdentifyNotice({
        tone: 'danger',
        text: `${kind === 'customer' ? 'مشتری' : 'سرنخ'} ساخته شد اما اتصال آن به تماس انجام نشد${
          err instanceof ApiError ? ` (${err.envelope?.error?.message ?? err.message})` : ''
        }.`,
      });
    }
    dashboard.reload();
    callDetail.reload();
  }

  const columns: V2GridColumn<CommunicationDashboardItem>[] = [
    {
      key: 'contact',
      header: 'مخاطب',
      width: '200px',
      render: (row) => {
        const contact = callContactOf(row);
        if (contact.kind === 'unknown') {
          return (
            <span className="call-base__contact">
              <bdi className="call-base__phone">{contact.phone}</bdi>
              <V2Button
                variant="ghost"
                size="sm"
                onClick={(event) => {
                  event.stopPropagation();
                  setIdentifyNotice(null);
                  setIdentify({ row, step: 'choose' });
                }}
                aria-label={`افزودن ${contact.phone} به سیستم`}
                title="افزودن به مشتریان یا سرنخ‌ها"
              >
                <Icon name="plus" size="sm" />
              </V2Button>
            </span>
          );
        }
        return (
          <span className="call-base__contact">
            <PartyLink base={base} kind={contact.kind} partyRef={contact.ref} inRow>
              {contact.label}
            </PartyLink>
            {contact.kind === 'lead' ? <V2Badge tone="warn">سرنخ</V2Badge> : null}
          </span>
        );
      },
    },
    {
      key: 'salesYar',
      header: 'تحلیل',
      width: '140px',
      render: (row) => (
        <V2Button
          variant="primary"
          className="call-base__yar-btn"
          size="sm"
          onClick={(event) => {
            event.stopPropagation();
            openDetail(row.ref, 'analysis');
          }}
          aria-label={`تحلیل فروش‌یار برای تماس ${row.ref}`}
        >
          <YarFace />
          تحلیل فروش‌یار
        </V2Button>
      ),
    },
    {
      key: 'direction',
      header: 'جهت تماس',
      width: '100px',
      render: (row) => (
        <V2Badge tone={row.direction === 'inbound' ? 'info' : 'neutral'}>
          {row.direction === 'inbound' ? 'ورودی' : 'خروجی'}
        </V2Badge>
      ),
    },
    {
      key: 'operator',
      header: 'اپراتور',
      width: '120px',
      render: (row) => <span>{row.operator ?? 'تخصیص داده نشده'}</span>,
    },
    {
      key: 'date',
      header: 'تاریخ و ساعت',
      width: '150px',
      render: (row) => (
        <span className="call-base__date">
          <strong>{formatDate(row.latestEventAt)}</strong>
          <small className="call-base__mono">{row.ref}</small>
        </span>
      ),
    },
    {
      key: 'queueOrExtension',
      header: 'صف / داخلی',
      width: '100px',
      render: (row) => <span>{row.queueOrExtension ?? '—'}</span>,
    },
    {
      key: 'waitDuration',
      header: 'مدت انتظار',
      width: '90px',
      render: (row) => (
        <span className="call-base__mono">{formatDuration(row.waitDurationSeconds)}</span>
      ),
    },
    {
      key: 'duration',
      header: 'مدت مکالمه',
      width: '90px',
      render: (row) => (
        <span className="call-base__mono">{formatDuration(row.durationSeconds)}</span>
      ),
    },
    {
      key: 'status',
      header: 'وضعیت تماس',
      width: '120px',
      render: (row) => (
        <V2Badge tone={eventTone(row.latestEvent)}>{EVENT_LABELS[row.latestEvent]}</V2Badge>
      ),
    },
    {
      key: 'provider',
      header: 'ارائه‌دهنده',
      width: '110px',
      render: (row) => <span>{row.provider ?? '—'}</span>,
    },
    {
      key: 'recordingStatus',
      header: 'ضبط مکالمه',
      width: '110px',
      render: (row) => (
        <V2Badge tone={recordingTone(row.recordingStatus)}>
          {RECORDING_STATUS_LABELS[row.recordingStatus] ?? 'بدون ضبط'}
        </V2Badge>
      ),
    },
    {
      key: 'transcriptStatus',
      header: 'متن مکالمه',
      width: '110px',
      render: (row) => (
        <V2Badge tone={recordingTone(row.transcriptStatus)}>
          {TRANSCRIPT_STATUS_LABELS[row.transcriptStatus] ?? 'بدون متن'}
        </V2Badge>
      ),
    },
    {
      key: 'analysisStatus',
      header: 'تحلیل AI',
      width: '120px',
      render: (row) => (
        <V2Badge tone={analysisTone(row.analysisStatus)}>
          {ANALYSIS_STATUS_LABELS[row.analysisStatus] ?? 'در انتظار'}
        </V2Badge>
      ),
    },
    {
      key: 'outcome',
      header: 'نتیجه تماس',
      width: '130px',
      render: (row) => (
        <span>{row.outcome ? (OUTCOME_LABELS[row.outcome] ?? row.outcome) : '—'}</span>
      ),
    },
    {
      key: 'nextAction',
      header: 'اقدام بعدی پیشنهادی',
      width: '160px',
      render: (row) => <span className="call-base__next-action">{row.nextAction ?? '—'}</span>,
    },
  ];

  return (
    <main className="call-base" data-module="crm">
      <header className="call-base__header">
        <div>
          <div className="call-base__eyebrow">مرکز ارتباطات مشتری</div>
          <h1>پایگاه تماس</h1>
          <p>
            داشبورد مدیریتی جامع تماس‌های ورودی و خروجی، تحلیل هوشمند صوت مکالمات و عملکرد اپراتورها
          </p>
        </div>
        <div className="call-base__header-actions">
          <V2Button variant="primary" size="sm" onClick={() => setUploadModalOpen(true)}>
            + افزودن تماس / آپلود ضبط
          </V2Button>
          <V2Badge tone="info">دفتر رسمی ارتباطات سازمان</V2Badge>
        </div>
      </header>

      {/* خلاصهٔ عملکرد از داده‌های واقعی — همان کاشی‌های هاب CRM */}
      <HubKpiGrid accent="crm">
        <HubKpi
          hero
          icon="phone"
          label="کل تماس‌ها"
          value={faNumber(summary.total)}
          weights={[summary.inbound, summary.outbound]}
          foot={`ورودی ${faNumber(summary.inbound)}`}
          footEnd={`خروجی ${faNumber(summary.outbound)}`}
        />
        <HubKpi
          label="پاسخ‌داده‌شده"
          value={faNumber(summary.answered)}
          foot={`نرخ پاسخگویی ${faNumber(summary.answerRate)}٪`}
          progress={{ percent: Math.max(0, Math.min(100, Math.round(summary.answerRate))) }}
        />
        <HubKpi
          label="بی‌پاسخ"
          value={faNumber(summary.missed)}
          tone={summary.missed > 0 ? 'danger' : undefined}
          foot={
            summary.missed > 0
              ? `نیازمند پیگیری · ناموفق ${faNumber(summary.failed)}`
              : `بدون مورد باز · ناموفق ${faNumber(summary.failed)}`
          }
          footTone={summary.missed > 0 ? 'danger' : undefined}
        />
        <HubKpi
          label="تحلیل‌شده با AI"
          value={faNumber(summary.analyzed)}
          foot={`در انتظار تحلیل ${faNumber(summary.waitingAnalysis)}`}
          {...(summary.total > 0
            ? { progress: { percent: Math.round((summary.analyzed * 100) / summary.total) } }
            : {})}
        />
        <HubKpi
          label="میانگین مکالمه"
          value={formatDuration(summary.averageDurationSeconds)}
          foot="دقیقه:ثانیه"
        />
      </HubKpiGrid>

      {identifyNotice ? <V2Alert tone={identifyNotice.tone} title={identifyNotice.text} /> : null}
      <section className="call-base__workspace">
        <div className="call-base__grid">
          <V2DataGrid
            icon="phone"
            title="فهرست تماس‌های سازمان"
            subtitle={`${faNumber(dashboard.data.total)} رکورد مجاز`}
            search={{
              value: search,
              onChange: (value) => patchParams({ q: value || null, offset: null, call: null }),
              placeholder: 'جست‌وجوی نام مشتری، کد، شماره تلفن یا شناسه تماس…',
            }}
            filters={[
              {
                id: 'direction',
                label: 'جهت تماس',
                value: direction,
                options: [
                  { value: '', label: 'همه' },
                  { value: 'inbound', label: 'ورودی' },
                  { value: 'outbound', label: 'خروجی' },
                ],
                onChange: (value) =>
                  patchParams({ direction: value || null, offset: null, call: null }),
              },
              {
                id: 'status',
                label: 'وضعیت تماس',
                value: status,
                options: [
                  { value: '', label: 'همه' },
                  { value: 'completed', label: 'تکمیل‌شده' },
                  { value: 'answered', label: 'پاسخ‌داده‌شده' },
                  { value: 'missed', label: 'بی‌پاسخ' },
                  { value: 'failed', label: 'ناموفق' },
                ],
                onChange: (value) =>
                  patchParams({ status: value || null, offset: null, call: null }),
              },
              {
                id: 'hasRecording',
                label: 'ضبط مکالمه',
                value: hasRecording,
                options: [
                  { value: '', label: 'همه' },
                  { value: 'yes', label: 'دارای فایل ضبط' },
                  { value: 'no', label: 'بدون فایل ضبط' },
                ],
                onChange: (value) =>
                  patchParams({ hasRecording: value || null, offset: null, call: null }),
              },
              {
                id: 'analysis',
                label: 'وضعیت تحلیل AI',
                value: analysis,
                options: [
                  { value: '', label: 'همه' },
                  { value: 'completed', label: 'تحلیل‌شده' },
                  { value: 'pending', label: 'در انتظار تحلیل' },
                  { value: 'waiting_for_transcript', label: 'در انتظار متن/صوت' },
                  { value: 'failed', label: 'تحلیل ناموفق' },
                  { value: 'needs_review', label: 'نیازمند بازبینی' },
                ],
                onChange: (value) =>
                  patchParams({ analysis: value || null, offset: null, call: null }),
              },
              {
                id: 'riskLevel',
                label: 'سطح ریسک AI',
                value: riskLevel,
                options: [
                  { value: '', label: 'همه' },
                  { value: 'low', label: 'ریسک کم' },
                  { value: 'medium', label: 'ریسک متوسط' },
                  { value: 'high', label: 'ریسک بالا' },
                  { value: 'critical', label: 'بحرانی' },
                ],
                onChange: (value) =>
                  patchParams({ riskLevel: value || null, offset: null, call: null }),
              },
            ]}
            columns={columns}
            rows={dashboard.data.items}
            rowKey={(row) => row.ref}
            selectedKey={selectedRef || null}
            onRowClick={(row) => openDetail(row.ref, 'info')}
            rowTone={(row) => eventTone(row.latestEvent)}
            serverPage={{
              offset: dashboard.data.offset,
              limit: dashboard.data.limit,
              total: dashboard.data.total,
              onChange: (nextOffset) =>
                patchParams({ offset: nextOffset ? String(nextOffset) : null, call: null }),
            }}
            storageKey="crm-manager-call-dashboard-v2"
            zebra
            maxHeight="680px"
            emptyState={
              <V2EmptyState
                icon="phone"
                title="تماسی با این فیلترها یافت نشد"
                description="عبارت جست‌وجوی یا فیلترهای انتخابی را تغییر دهید."
              />
            }
            ariaLabel="فهرست مدیریتی تماس‌های ورودی و خروجی سازمان"
          />
        </div>

        {/* Call details + Sales-Yar analysis: modal opened from a row or its «تحلیل فروش‌یار» button */}
      </section>
      <V2Modal
        open={identify?.step === 'choose'}
        title="افزودن مخاطب به سیستم"
        size="sm"
        onClose={() => setIdentify(null)}
      >
        <p className="call-base__identify-lead">
          شمارهٔ <bdi className="call-base__phone">{identify?.row.endpoint}</bdi> هنوز در سیستم ثبت
          نشده است. این مخاطب چیست؟
        </p>
        <div className="call-base__identify-choices">
          <V2Button
            variant="primary"
            onClick={() => identify && setIdentify({ row: identify.row, step: 'customer' })}
          >
            مشتری
          </V2Button>
          <V2Button
            variant="secondary"
            onClick={() => identify && setIdentify({ row: identify.row, step: 'lead' })}
          >
            سرنخ (لید)
          </V2Button>
        </div>
      </V2Modal>
      <CustomerCreateDialog
        open={identify?.step === 'customer'}
        base={base}
        initial={{ kind: 'person', mobile: identify?.row.endpoint ?? '' }}
        onClose={() => setIdentify(null)}
        onCreated={(ref) => void linkIdentified('customer', ref)}
      />
      <LeadFormPanel
        base={base}
        open={identify?.step === 'lead'}
        editing={null}
        presetMobile={identify?.row.endpoint}
        onClose={() => setIdentify(null)}
        onSaved={(lead) => void linkIdentified('lead', lead.ref)}
        onOpenLead={() => undefined}
      />
      <V2Modal
        open={Boolean(selectedRef)}
        title={
          <span className="call-base__modal-title">
            <YarFace size={28} />
            جزئیات تماس و تحلیل فروش‌یار
          </span>
        }
        size="lg"
        onClose={closeDetail}
      >
        <div id="call-detail-panel" aria-label="جزئیات تماس و تحلیل هوشمند">
          <p className="call-base__detail-ref">
            <bdi>{selected ? selected.ref : selectedRef}</bdi>
          </p>
          {callDetail.status === 'loading' ? (
            <div className="call-base__loading">در حال بارگذاری جزئیات تماس…</div>
          ) : selected ? (
            <div className="call-base__detail-content">
              <V2Tabs
                items={[
                  { id: 'info', label: 'اطلاعات تماس' },
                  { id: 'recording', label: 'فایل مکالمه' },
                  { id: 'transcript', label: 'متن مکالمه' },
                  { id: 'analysis', label: 'تحلیل فروش‌یار' },
                ]}
                activeId={detailTab}
                onChange={setDetailTab}
              />

              {actionError ? <div className="call-base__error-banner">{actionError}</div> : null}

              {/* Tab 1: Call Information */}
              {detailTab === 'info' ? (
                <div className="call-base__detail-body">
                  <dl className="call-base__facts">
                    <div>
                      <dt>مشتری</dt>
                      <dd>
                        {selected.partyCode ? (
                          <PartyLink base={base} partyRef={selected.partyCode}>
                            {selected.partyName} (کد: {selected.partyCode})
                          </PartyLink>
                        ) : (
                          <span>{selected.partyName || 'طرف حساب نامشخص'}</span>
                        )}
                      </dd>
                    </div>
                    {selected.contactResolutionStatus &&
                    selected.contactResolutionStatus !== 'identified' ? (
                      <div>
                        <dt>شناسایی هویت مخاطب</dt>
                        <dd>
                          <V2Badge
                            tone={
                              selected.contactResolutionStatus === 'pending_identity'
                                ? 'warn'
                                : 'neutral'
                            }
                          >
                            {selected.contactResolutionStatus === 'pending_identity'
                              ? 'در انتظار شناسایی هویت'
                              : 'مخاطب ناشناس'}
                          </V2Badge>
                        </dd>
                      </div>
                    ) : null}
                    {selected.leadRef ? (
                      <div>
                        <dt>سرنخ فروش مرتبط (Lead)</dt>
                        <dd>
                          <PartyLink base={base} kind="lead" partyRef={selected.leadRef}>
                            <bdi>{selected.leadTitle || selected.leadRef}</bdi>
                          </PartyLink>
                        </dd>
                      </div>
                    ) : null}
                    <div>
                      <dt>شماره تماس کامل</dt>
                      <dd>
                        <bdi className="call-base__phone">{selected.endpoint}</bdi>
                      </dd>
                    </div>
                    <div>
                      <dt>نوع تماس</dt>
                      <dd>
                        <V2Badge tone={selected.direction === 'inbound' ? 'info' : 'neutral'}>
                          {selected.direction === 'inbound' ? 'تماس ورودی' : 'تماس خروجی'}
                        </V2Badge>
                      </dd>
                    </div>
                    <div>
                      <dt>اپراتور</dt>
                      <dd>{selected.operator ?? 'تخصیص داده نشده'}</dd>
                    </div>
                    <div>
                      <dt>زمان شروع</dt>
                      <dd>{formatDate(selected.startedAt ?? selected.latestEventAt)}</dd>
                    </div>
                    <div>
                      <dt>مدت انتظار</dt>
                      <dd>{formatDuration(selected.waitDurationSeconds)}</dd>
                    </div>
                    <div>
                      <dt>مدت مکالمه</dt>
                      <dd>{formatDuration(selected.durationSeconds)}</dd>
                    </div>
                    <div>
                      <dt>وضعیت نهایی</dt>
                      <dd>
                        <V2Badge tone={eventTone(selected.latestEvent)}>
                          {EVENT_LABELS[selected.latestEvent]}
                        </V2Badge>
                      </dd>
                    </div>
                    <div>
                      <dt>ارائه‌دهنده VoIP</dt>
                      <dd>{selected.provider ?? 'ثبت نشده'}</dd>
                    </div>
                    {selected.reasonCode ? (
                      <div>
                        <dt>علت قطعی / خطا</dt>
                        <dd className="call-base__error-text">{selected.reasonCode}</dd>
                      </div>
                    ) : null}
                  </dl>
                  <div className="call-base__actions-bar">
                    {selected.partyCode ? (
                      <Link
                        className="sk-btn sk-btn--secondary sk-btn--sm"
                        to={`${base}/crm/customers/${encodeURIComponent(selected.partyCode)}?tab=communications`}
                      >
                        مشاهده سوابق در Customer 360
                      </Link>
                    ) : null}
                  </div>
                </div>
              ) : null}

              {/* Tab 2: Recording Player */}
              {detailTab === 'recording' ? (
                <div className="call-base__detail-body">
                  {selected.recording.status === 'available' || selected.recording.hasFile ? (
                    <div className="call-base__recording-card">
                      <div className="call-base__recording-header">
                        <div>
                          <strong>ضبط مکالمه صوتی</strong>
                          <p>
                            مدت زمان:{' '}
                            {formatDuration(
                              selected.recording.durationSeconds ?? selected.durationSeconds,
                            )}
                          </p>
                        </div>
                        <V2Badge tone="success">آماده پخش</V2Badge>
                      </div>
                      <AudioPlayer
                        streamUrl={`${base}/api/v1/crm/communications/${encodeURIComponent(
                          selected.ref,
                        )}/recording/stream`}
                        downloadUrl={`${base}/api/v1/crm/communications/${encodeURIComponent(
                          selected.ref,
                        )}/recording/download`}
                      />
                    </div>
                  ) : (
                    <V2EmptyState
                      icon="phone"
                      title="فایل مکالمه برای این تماس وجود ندارد"
                      description={
                        selected.recording.status === 'processing'
                          ? 'فایل مکالمه در حال انتقال از سرویس‌دهنده VoIP است.'
                          : 'برای این تماس فایل صوتی توسط سرویس‌دهنده ضبط نشده یا دسترسی به آن فراهم نیست.'
                      }
                      compact
                    />
                  )}
                </div>
              ) : null}

              {/* Tab 3: Transcript Viewer */}
              {detailTab === 'transcript' ? (
                <div className="call-base__detail-body">
                  {selected.transcript.status === 'completed' &&
                  (selected.transcript.rawText ||
                    (selected.transcript.segments?.length ?? 0) > 0) ? (
                    <TranscriptView
                      rawText={selected.transcript.rawText}
                      segments={selected.transcript.segments ?? []}
                    />
                  ) : (
                    <V2EmptyState
                      icon="phone"
                      title="متن مکالمه ثبت نشده است"
                      description={
                        selected.transcript.status === 'queued' ||
                        selected.transcript.status === 'processing'
                          ? 'تبدیل صوت به متن در صف پردازش قرار دارد.'
                          : 'هنوز متن تبدیل‌شده‌ای برای این مکالمه موجود نیست.'
                      }
                      compact
                    />
                  )}
                </div>
              ) : null}

              {/* Tab 4: AI Analysis */}
              {detailTab === 'analysis' ? (
                <div className="call-base__detail-body">
                  <div className="call-base__analysis-header">
                    <div>
                      <strong>وضعیت تحلیل:</strong>
                      <V2Badge tone={analysisTone(selected.analysis.status)}>
                        {ANALYSIS_STATUS_LABELS[selected.analysis.status] ??
                          selected.analysis.status}
                      </V2Badge>
                    </div>
                    {selected.analysis.completedAt ? (
                      <small>{formatDate(selected.analysis.completedAt)}</small>
                    ) : null}
                  </div>

                  {actionLoading ||
                  selected.analysis.status === 'processing' ||
                  selected.analysis.status === 'queued' ? (
                    <AnalyzingPanel />
                  ) : null}

                  {selected.analysis.status === 'pending_confirmation' ? (
                    <div
                      className="call-base__confirmation-banner"
                      style={{ marginBottom: '1rem' }}
                    >
                      <V2Alert
                        tone="warn"
                        title="تأیید مصرف توکن برای مکالمه طولانی"
                        action={
                          <V2Button
                            variant="primary"
                            size="sm"
                            disabled={actionLoading}
                            onClick={() => handleTriggerAnalysis(false, true)}
                          >
                            {actionLoading ? 'در حال ثبت…' : 'تأیید مصرف و آغاز تحلیل'}
                          </V2Button>
                        }
                      >
                        تعداد توکن‌های تخمینی این مکالمه (
                        {faNumber(selected.analysis.estimatedTokens ?? 0)} توکن) از حد مجاز ۲۰,۰۰۰
                        توکن بیشتر است. جهت پیشگیری از کسر ناخواسته اعتبار، اجرای تحلیل هوشمند
                        نیازمند تأیید کاربر مجاز است.
                      </V2Alert>
                    </div>
                  ) : null}

                  {selected.analysis.status === 'insufficient_balance' ? (
                    <div
                      className="call-base__confirmation-banner"
                      style={{ marginBottom: '1rem' }}
                    >
                      <V2Alert
                        tone="danger"
                        title="کسری اعتبار کیف پول هوش مصنوعی"
                        action={
                          <V2Button variant="primary" size="sm" onClick={() => setTopupOpen(true)}>
                            افزایش اعتبار
                          </V2Button>
                        }
                      >
                        اعتبار کیف پول سازمانی برای تحلیل هوشمند این مکالمه کافی نیست. اعتبار را
                        افزایش دهید و سپس تحلیل را دوباره اجرا کنید.
                      </V2Alert>
                    </div>
                  ) : null}

                  {selected.analysis.status === 'completed' ? (
                    <div className="call-base__analysis-grid">
                      <div className="call-base__analysis-box">
                        <h4>خلاصه مکالمه</h4>
                        <p>{selected.analysis.summary}</p>
                      </div>

                      <div className="call-base__analysis-chips">
                        <div>
                          <span className="call-base__chip-label">موضوع اصلی:</span>
                          <strong>{selected.analysis.intent}</strong>
                        </div>
                        <div>
                          <span className="call-base__chip-label">احساس مشتری:</span>
                          <V2Badge tone={sentimentTone(selected.analysis.sentiment)}>
                            {SENTIMENT_LABELS[selected.analysis.sentiment ?? ''] ??
                              selected.analysis.sentiment}
                          </V2Badge>
                        </div>
                        <div>
                          <span className="call-base__chip-label">نتیجه تماس:</span>
                          <V2Badge tone="info">
                            {OUTCOME_LABELS[selected.analysis.outcome ?? ''] ??
                              selected.analysis.outcome}
                          </V2Badge>
                        </div>
                        <div>
                          <span className="call-base__chip-label">میزان رضایت:</span>
                          <strong>
                            {faNumber(selected.analysis.satisfactionScore ?? 0)} از ۵ ستاره
                          </strong>
                        </div>
                        <div>
                          <span className="call-base__chip-label">ریسک ریزش:</span>
                          <V2Badge tone={riskTone(selected.analysis.churnRisk)}>
                            {RISK_LABELS[selected.analysis.churnRisk ?? ''] ??
                              selected.analysis.churnRisk}
                          </V2Badge>
                        </div>
                      </div>

                      {(selected.analysis.sentimentTrajectory?.length ?? 0) > 0 ? (
                        <div className="call-base__trajectory">
                          <h4>روند تغییر احساس در طول مکالمه</h4>
                          <div className="call-base__trajectory-stages">
                            {(selected.analysis.sentimentTrajectory ?? []).map(
                              (st: CallSentimentStage, i: number) => (
                                <div key={i} className="call-base__trajectory-step">
                                  <span>{st.stage}</span>
                                  <V2Badge tone={sentimentTone(st.sentiment)}>
                                    {SENTIMENT_LABELS[st.sentiment] ?? st.sentiment}
                                  </V2Badge>
                                </div>
                              ),
                            )}
                          </div>
                        </div>
                      ) : null}

                      {(selected.analysis.objections?.length ?? 0) > 0 ? (
                        <div className="call-base__analysis-box">
                          <h4>اعتراض‌ها و مشکلات مطرح‌شده</h4>
                          <ul>
                            {(selected.analysis.objections ?? []).map((obj, i) => (
                              <li key={i}>{obj}</li>
                            ))}
                          </ul>
                        </div>
                      ) : null}

                      {(selected.analysis.agentCommitments?.length ?? 0) > 0 ? (
                        <div className="call-base__analysis-box">
                          <h4>تعهداتی که کارشناس داده است</h4>
                          <ul>
                            {(selected.analysis.agentCommitments ?? []).map((c, i) => (
                              <li key={i}>{c}</li>
                            ))}
                          </ul>
                        </div>
                      ) : null}

                      {selected.analysis.nextAction ? (
                        <div className="call-base__next-box">
                          <h4>اقدام بعدی پیشنهادی</h4>
                          <p>{selected.analysis.nextAction}</p>
                          <small>
                            مسئول: {selected.analysis.nextActionOwner ?? 'کارشناس فروش'}
                            {selected.analysis.nextActionDueAt
                              ? ` · مهلت: ${formatDate(selected.analysis.nextActionDueAt)}`
                              : ''}
                          </small>
                        </div>
                      ) : null}

                      <div className="call-base__analysis-meta">
                        <small>مدل: {selected.analysis.model ?? 'AI Engine'}</small>
                        <small>
                          ضریب اطمینان:{' '}
                          {faNumber(Math.round((selected.analysis.confidence ?? 0.9) * 100))}٪
                        </small>
                        {selected.analysis.totalTokens ? (
                          <small>
                            مصرف توکن: {faNumber(selected.analysis.totalTokens)} توکن
                            {selected.analysis.inputTokens !== undefined &&
                            selected.analysis.outputTokens !== undefined ? (
                              <span>
                                {' '}
                                (ورودی: {faNumber(selected.analysis.inputTokens)} / خروجی:{' '}
                                {faNumber(selected.analysis.outputTokens)})
                              </span>
                            ) : null}
                          </small>
                        ) : selected.analysis.estimatedTokens ? (
                          <small>
                            تخمین توکن: {faNumber(selected.analysis.estimatedTokens)} توکن
                          </small>
                        ) : null}
                      </div>
                    </div>
                  ) : actionLoading ||
                    selected.analysis.status === 'processing' ||
                    selected.analysis.status === 'queued' ? null : (
                    <V2EmptyState
                      icon="trend"
                      title={
                        selected.analysis.status === 'failed'
                          ? 'تحلیل هوشمند تماس با خطا مواجه شد'
                          : selected.analysis.status === 'waiting_for_transcript'
                            ? 'در انتظار صوت یا متن مکالمه'
                            : 'این مکالمه هنوز تحلیل نشده است'
                      }
                      description={
                        selected.analysis.failureMessage ||
                        'برای اجرای تحلیل هوشمند، فایل صوتی یا متن مکالمه لازم است.'
                      }
                      compact
                    />
                  )}

                  {/* Manager Analysis Controls */}
                  <div className="call-base__mgmt-actions">
                    {selected.analysis.status === 'pending_confirmation' ? (
                      <V2Button
                        variant="primary"
                        size="sm"
                        disabled={actionLoading}
                        onClick={() => handleTriggerAnalysis(false, true)}
                      >
                        {actionLoading ? 'در حال اجرا…' : 'تأیید مصرف و آغاز تحلیل'}
                      </V2Button>
                    ) : selected.analysis.status === 'completed' ? (
                      <>
                        <V2Button
                          variant="secondary"
                          size="sm"
                          disabled={actionLoading}
                          onClick={() => handleTriggerAnalysis(false)}
                        >
                          {actionLoading ? 'در حال اجرا…' : 'تحلیل مجدد با نسخه جدید مدل'}
                        </V2Button>
                        <V2Button
                          variant="ghost"
                          size="sm"
                          disabled={actionLoading}
                          onClick={handleReviewAnalysis}
                        >
                          علامت‌گذاری برای بررسی انسانی
                        </V2Button>
                      </>
                    ) : selected.analysis.status === 'failed' ? (
                      <V2Button
                        variant="primary"
                        size="sm"
                        disabled={actionLoading}
                        onClick={() => handleTriggerAnalysis(true)}
                      >
                        {actionLoading ? 'در حال ارسال…' : 'تلاش دوباره برای تحلیل'}
                      </V2Button>
                    ) : (
                      <V2Button
                        variant="primary"
                        size="sm"
                        disabled={actionLoading}
                        onClick={() => handleTriggerAnalysis(false)}
                      >
                        {actionLoading ? (
                          'در حال تحلیل…'
                        ) : (
                          <>
                            <YarFace size={20} />
                            تحلیل فروش‌یار
                          </>
                        )}
                      </V2Button>
                    )}
                  </div>
                </div>
              ) : null}
            </div>
          ) : (
            <V2EmptyState
              icon="phone"
              title="جزئیات این تماس در دسترس نیست"
              description="ممکن است تماس حذف شده یا دسترسی شما به آن محدود باشد."
              compact
            />
          )}
        </div>
      </V2Modal>
      <CreditTopupDialog slug={slug} open={topupOpen} onClose={() => setTopupOpen(false)} />
      <V2Modal
        open={uploadModalOpen}
        title="بارگذاری و ثبت مکالمه صوتی"
        size="md"
        onClose={() => {
          if (!uploadLoading) {
            setUploadModalOpen(false);
            setUploadError(null);
            setBatchResults(null);
          }
        }}
      >
        <form noValidate onSubmit={handleUploadSubmit}>
          <V2FormGrid>
            <V2FormField
              label="فایل صوتی مکالمه (تکی یا چندتایی)"
              required
              width="full"
              hint="فرمت‌های صوتی مجاز: MP3، WAV، M4A، AAC، OGG (حداکثر ۵۰ مگابایت)"
            >
              <input
                type="file"
                accept="audio/*,.mp3,.wav,.m4a,.aac,.ogg,.flac"
                multiple
                disabled={uploadLoading}
                onChange={(e) => {
                  const files = e.target.files ? Array.from(e.target.files) : [];
                  setUploadFiles(files);
                  setUploadError(null);
                  setBatchResults(null);
                }}
                className="sk-input"
              />
              {uploadFiles.length > 0 ? (
                <div
                  style={{
                    marginTop: '0.5rem',
                    fontSize: '0.85rem',
                    color: 'var(--sk-text-muted)',
                  }}
                >
                  {uploadFiles.length === 1 ? (
                    <span>
                      فایل انتخاب‌شده: {uploadFiles[0]!.name} (
                      {Math.round(uploadFiles[0]!.size / 1024)} کیلوبایت)
                    </span>
                  ) : (
                    <span>تعداد {faNumber(uploadFiles.length)} فایل انتخاب شد (حالت دسته‌ای)</span>
                  )}
                </div>
              ) : null}
            </V2FormField>

            <V2FormField
              label="شماره تماس طرف مقابل"
              required
              width="medium"
              hint="شماره مشتری یا مخاطب، مثلاً ۰۹۱۲۱۲۳۴۵۶۷"
            >
              <input
                type="tel"
                value={uploadExternalNumber}
                onChange={(e) => setUploadExternalNumber(e.target.value)}
                placeholder="09121234567"
                disabled={uploadLoading}
                className="sk-input ltr"
              />
            </V2FormField>

            <V2FormField label="جهت تماس" width="short">
              <Select
                value={uploadDirection}
                disabled={uploadLoading}
                onChange={(e) => setUploadDirection(e.target.value as 'inbound' | 'outbound')}
              >
                <option value="inbound">ورودی</option>
                <option value="outbound">خروجی</option>
              </Select>
            </V2FormField>

            <V2FormField label="داخلی / اپراتور" width="short" hint="اختیاری (پیش‌فرض ۱۰۱)">
              <input
                type="text"
                value={uploadExtension}
                onChange={(e) => setUploadExtension(e.target.value)}
                placeholder="101"
                disabled={uploadLoading}
                className="sk-input ltr"
              />
            </V2FormField>

            <p className="call-base__upload-note">
              تحلیل مکالمه پس از بارگذاری خودکار انجام نمی‌شود؛ فقط با دکمهٔ «تحلیل فروش‌یار» روی
              همان تماس اجرا می‌شود.
            </p>
          </V2FormGrid>

          {uploadError ? (
            <div style={{ marginTop: '1rem' }}>
              <V2Alert tone="danger" title="خطا در بارگذاری">
                {uploadError}
              </V2Alert>
            </div>
          ) : null}

          {batchResults ? (
            <div style={{ marginTop: '1rem' }}>
              <V2Alert
                tone={batchResults.failed === 0 ? 'success' : 'warn'}
                title={`نتیجه بارگذاری دسته‌ای: ${faNumber(batchResults.successful)} موفق از ${faNumber(batchResults.total)}`}
              >
                {batchResults.failed > 0
                  ? `${faNumber(batchResults.failed)} فایل با خطا مواجه شد. جزئیات را در فهرست تماس‌ها بررسی کنید.`
                  : 'تمامی فایل‌ها با موفقیت ثبت و ذخیره شدند.'}
              </V2Alert>
            </div>
          ) : null}

          <V2FormActions>
            <V2Button
              type="button"
              variant="ghost"
              disabled={uploadLoading}
              onClick={() => setUploadModalOpen(false)}
            >
              انصراف
            </V2Button>
            <V2Button
              type="submit"
              variant="primary"
              disabled={uploadLoading || uploadFiles.length === 0 || !uploadExternalNumber.trim()}
            >
              {uploadLoading
                ? 'در حال بارگذاری و ذخیره…'
                : uploadFiles.length > 1
                  ? `بارگذاری دسته‌ای (${faNumber(uploadFiles.length)} فایل)`
                  : 'بارگذاری و ذخیره مکالمه'}
            </V2Button>
          </V2FormActions>
        </form>
      </V2Modal>
    </main>
  );
}
